import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes } from 'firebase/storage';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconPlus, IconTrash, IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { storage } from '../../firebase';
import { callFn, inputStyle, labelStyle } from './contractsApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emptySigner = () => ({ key: Math.random().toString(36).slice(2), name: '', email: '', role: '' });

export default function NewContract() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [signingOrder, setSigningOrder] = useState('parallel');
  const [signers, setSigners] = useState([emptySigner()]);
  const [senderName, setSenderName] = useState(userProfile?.name && userProfile.name !== user?.email ? userProfile.name : 'Dan Derry');
  const [senderRole, setSenderRole] = useState('Founder, Agriware');
  const myEmail = (user?.email || '').toLowerCase();
  const effectiveOrder = signers.length > 1 ? signingOrder : 'parallel';
  const [paymentOn, setPaymentOn] = useState(false);
  const [createProject, setCreateProject] = useState(true);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('gbp');
  const [plan, setPlan] = useState('full');
  const [payerKey, setPayerKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const updateSigner = (key, field, value) => setSigners(s => s.map(x => x.key === key ? { ...x, [field]: value } : x));
  const removeSigner = (key) => setSigners(s => s.filter(x => x.key !== key));
  const move = (i, dir) => setSigners(s => {
    const next = [...s]; const j = i + dir;
    if (j < 0 || j >= next.length) return s;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const validate = () => {
    if (!file) return 'Choose a PDF to upload.';
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return 'The file must be a PDF.';
    if (file.size > 25 * 1024 * 1024) return 'The PDF must be 25 MB or smaller.';
    if (!title.trim()) return 'Enter a title.';
    if (!clientName.trim()) return 'Enter a client name.';
    if (!senderName.trim() || !senderRole.trim()) return 'Enter your name and role.';
    if (signers.length === 0) return 'Add at least one client signer.';
    for (const [i, s] of signers.entries()) {
      if (!s.name.trim() || !s.email.trim() || !s.role.trim()) return `Client signer ${i + 1}: name, email and role are all required.`;
      if (!EMAIL_RE.test(s.email.trim())) return `Client signer ${i + 1}: email is not valid.`;
      if (s.email.trim().toLowerCase() === myEmail) return `Client signer ${i + 1} uses your own email. You are already on the contract.`;
    }
    const emails = signers.map(s => s.email.trim().toLowerCase());
    if (new Set(emails).size !== emails.length) return 'Each signer needs a different email address.';
    if (paymentOn) {
      const minor = toMinor(amount);
      if (!minor) return 'Enter a valid payment amount, e.g. 1500 or 1500.00.';
      if (payerIndex() < 0) return 'Choose which signer pays.';
    }
    return null;
  };

  const payerIndex = () => signers.findIndex(s => s.key === payerKey);

  const save = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true); setError(null);
    try {
      const uploadPath = `contracts/uploads/${user.uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.pdf`;
      await uploadBytes(ref(storage, uploadPath), file, { contentType: 'application/pdf' });
      const res = await callFn('createContract', {
        title: title.trim(), clientName: clientName.trim(), signingOrder: effectiveOrder, uploadPath, fileName: file.name,
        sender: { name: senderName.trim(), role: senderRole.trim() },
        signers: signers.map((s, i) => ({ name: s.name.trim(), email: s.email.trim(), role: s.role.trim(), order: i })),
        createClientProject: createProject,
        payment: paymentOn ? { amount: toMinor(amount), currency, plan, payerIndex: payerIndex() } : null,
      });
      navigate(`/contracts/${res.contractId}`);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Layout>
      <PageHeader title="New contract" subtitle="Upload a PDF and add the people who need to sign it"
        actions={<>
          <Button variant="secondary" onClick={() => navigate('/contracts')}>Cancel</Button>
          <Button loading={saving} onClick={save}>Save as draft</Button>
        </>}
      />
      <div style={{ padding: 28, maxWidth: 860, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12 }}>{error}</div>}

        <Card>
          <div style={{ fontSize: 14, marginBottom: 14 }}>Document</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>PDF contract</label>
              <input type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files[0] || null)} style={{ fontSize: 12 }} />
            </div>
            <div>
              <label style={labelStyle}>Title</label>
              <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Master Services Agreement" />
            </div>
            <div>
              <label style={labelStyle}>Client</label>
              <input style={inputStyle} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Paragon Ltd" />
            </div>
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 14 }}>Signers</div>
            <Button size="sm" variant="secondary" icon={<IconPlus size={12} stroke={2} />} onClick={() => setSigners(s => [...s, emptySigner()])}>Add client signer</Button>
          </div>

          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>You (sign first, in HQ, before the invites go out)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 62px', gap: 8, marginBottom: 18 }}>
            <input style={inputStyle} value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="Your name" />
            <input style={{ ...inputStyle, background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }} value={user?.email || ''} disabled />
            <input style={inputStyle} value={senderRole} onChange={e => setSenderRole(e.target.value)} placeholder="Founder, Agriware" />
          </div>

          {signers.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Client signing order (after you have signed)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['parallel', 'Parallel', 'All client signers get their link at once'], ['sequential', 'Sequential', 'Client signers sign one after another, in the order below']].map(([v, l, d]) => (
                  <div key={v} onClick={() => setSigningOrder(v)} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                    border: signingOrder === v ? '1px solid var(--color-brand)' : '0.5px solid var(--color-border-medium)',
                    background: signingOrder === v ? 'var(--color-brand-light)' : 'transparent',
                  }}>
                    <div style={{ fontSize: 13 }}>{l}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{d}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>Client signers</div>
          {signers.map((s, i) => (
            <div key={s.key} style={{ display: 'grid', gridTemplateColumns: `${effectiveOrder === 'sequential' ? '28px ' : ''}1fr 1fr 1fr auto`, gap: 8, alignItems: 'end', marginBottom: 10 }}>
              {effectiveOrder === 'sequential' && (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', height: 34, display: 'flex', alignItems: 'center' }}>{i + 1}.</div>
              )}
              <div>
                {i === 0 && <label style={labelStyle}>Name</label>}
                <input style={inputStyle} value={s.name} onChange={e => updateSigner(s.key, 'name', e.target.value)} />
              </div>
              <div>
                {i === 0 && <label style={labelStyle}>Email</label>}
                <input style={inputStyle} type="email" value={s.email} onChange={e => updateSigner(s.key, 'email', e.target.value)} />
              </div>
              <div>
                {i === 0 && <label style={labelStyle}>Role</label>}
                <input style={inputStyle} value={s.role} onChange={e => updateSigner(s.key, 'role', e.target.value)} placeholder="Director, Paragon Ltd" />
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                {effectiveOrder === 'sequential' && <>
                  <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} icon={<IconArrowUp size={13} stroke={1.5} />} />
                  <Button size="sm" variant="ghost" disabled={i === signers.length - 1} onClick={() => move(i, 1)} icon={<IconArrowDown size={13} stroke={1.5} />} />
                </>}
                <Button size="sm" variant="ghost" disabled={signers.length === 1} onClick={() => removeSigner(s.key)} icon={<IconTrash size={13} stroke={1.5} />} />
              </div>
            </div>
          ))}
        </Card>

        <Card>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={createProject} onChange={e => setCreateProject(e.target.checked)} />
            Create client project when signed
          </label>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '4px 0 0 24px' }}>
            Sets up the client delivery portal (closed) with the client signers ready to invite. Untick for NDAs and one-off agreements.
          </div>
        </Card>

        <Card>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={paymentOn} onChange={e => setPaymentOn(e.target.checked)} />
            Request payment
          </label>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '4px 0 0 24px' }}>
            The payer is taken to Stripe as soon as they sign. Make sure these terms match the contract PDF.
          </div>
          {paymentOn && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr', gap: 14, marginTop: 16 }}>
              <div>
                <label style={labelStyle}>Total amount</label>
                <input style={inputStyle} inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="3000.00" />
              </div>
              <div>
                <label style={labelStyle}>Currency</label>
                <select style={inputStyle} value={currency} onChange={e => setCurrency(e.target.value)}>
                  <option value="gbp">GBP</option>
                  <option value="eur">EUR</option>
                  <option value="usd">USD</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Payer</label>
                <select style={inputStyle} value={payerKey || ''} onChange={e => setPayerKey(e.target.value || null)}>
                  <option value="">Choose signer...</option>
                  {signers.map((s, i) => <option key={s.key} value={s.key}>{s.name || `Signer ${i + 1}`}{s.role ? ` (${s.role})` : ''}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Payment plan</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['full', 'Pay in full', 'One payment when they sign'], ['instalments_3', '3 monthly instalments', 'First now, then two more a month apart, collected automatically']].map(([v, l, d]) => (
                    <div key={v} onClick={() => setPlan(v)} style={{
                      flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                      border: plan === v ? '1px solid var(--color-brand)' : '0.5px solid var(--color-border-medium)',
                      background: plan === v ? 'var(--color-brand-light)' : 'transparent',
                    }}>
                      <div style={{ fontSize: 13 }}>{l}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{d}</div>
                    </div>
                  ))}
                </div>
                {toMinor(amount) > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 10 }}>{describePlan(toMinor(amount), currency, plan)}</div>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}

function toMinor(v) {
  const t = String(v).replace(/[,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return 0;
  return Math.round(parseFloat(t) * 100);
}

function describePlan(minor, currency, plan) {
  const f = (m) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(m / 100);
  if (plan === 'full') return `The client pays ${f(minor)} in one payment.`;
  const base = Math.floor(minor / 3);
  const first = minor - 2 * base;
  return first === base
    ? `The client pays 3 monthly instalments of ${f(base)}.`
    : `The client pays ${f(first)} now, then 2 monthly instalments of ${f(base)}.`;
}
